import type {
  GridColDef,
  GridFilterItem,
  GridFilterOperator,
  GridStateColDef,
} from "@material-ui/x-grid";
import { GridFilterInputValue } from "@material-ui/x-grid";

function toSearchable(value: unknown): string {
  return value == null ? "" : String(value).toLowerCase();
}

function getFilterValue(filterItem: GridFilterItem): string | null {
  const value = filterItem.value;
  if (value == null) return null;
  const text = String(value).toLowerCase();
  return text;
}

function makeStringValueOperator({
  label,
  value,
  predicate,
}: {
  label: string;
  value: string;
  predicate: (cell: string, filter: string) => boolean;
}): GridFilterOperator {
  return {
    label,
    value,
    getApplyFilterFn: (
      filterItem: GridFilterItem,
      _column: GridStateColDef
    ) => {
      const filterValue = getFilterValue(filterItem);
      if (filterValue == null) return null;
      return (params) => predicate(toSearchable(params.value), filterValue);
    },
    InputComponent: GridFilterInputValue,
  };
}

function getNumericFilterValue(filterItem: GridFilterItem): number | null {
  const value = filterItem.value;
  if (value == null || value === "") return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  return numeric;
}

function toNumeric(value: unknown): number | null {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  return numeric;
}

function makeNumberValueOperator({
  label,
  value,
  predicate,
}: {
  label: string;
  value: string;
  predicate: (cell: number, filter: number) => boolean;
}): GridFilterOperator {
  return {
    label,
    value,
    getApplyFilterFn: (
      filterItem: GridFilterItem,
      _column: GridStateColDef
    ) => {
      const filterValue = getNumericFilterValue(filterItem);
      if (filterValue == null) return null;
      return (params) => {
        const cell = toNumeric(params.value);
        if (cell == null) return false;
        return predicate(cell, filterValue);
      };
    },
    InputComponent: GridFilterInputValue,
  };
}

export function getGridStringOperatorsWithNotContains(): GridFilterOperator[] {
  return [
    makeStringValueOperator({
      label: "contains",
      value: "contains",
      predicate: (cell, filter) => cell.includes(filter),
    }),
    makeStringValueOperator({
      label: "does not contain",
      value: "notContains",
      predicate: (cell, filter) => !cell.includes(filter),
    }),
    makeStringValueOperator({
      label: "equals",
      value: "equals",
      predicate: (cell, filter) => cell === filter,
    }),
    makeStringValueOperator({
      label: "does not equal",
      value: "doesNotEqual",
      predicate: (cell, filter) => cell !== filter,
    }),
    makeStringValueOperator({
      label: "starts with",
      value: "startsWith",
      predicate: (cell, filter) => cell.startsWith(filter),
    }),
    makeStringValueOperator({
      label: "ends with",
      value: "endsWith",
      predicate: (cell, filter) => cell.endsWith(filter),
    }),
    {
      label: "is empty",
      value: "isEmpty",
      getApplyFilterFn: () => {
        return (params) => params.value == null || params.value === "";
      },
    },
    {
      label: "is not empty",
      value: "isNotEmpty",
      getApplyFilterFn: () => {
        return (params) => params.value != null && params.value !== "";
      },
    },
  ];
}

export function getGridNumberOperatorsWithZeroSupport(): GridFilterOperator[] {
  return [
    makeNumberValueOperator({
      label: "=",
      value: "=",
      predicate: (cell, filter) => cell === filter,
    }),
    makeNumberValueOperator({
      label: "!=",
      value: "!=",
      predicate: (cell, filter) => cell !== filter,
    }),
    makeNumberValueOperator({
      label: ">",
      value: ">",
      predicate: (cell, filter) => cell > filter,
    }),
    makeNumberValueOperator({
      label: ">=",
      value: ">=",
      predicate: (cell, filter) => cell >= filter,
    }),
    makeNumberValueOperator({
      label: "<",
      value: "<",
      predicate: (cell, filter) => cell < filter,
    }),
    makeNumberValueOperator({
      label: "<=",
      value: "<=",
      predicate: (cell, filter) => cell <= filter,
    }),
    {
      label: "is empty",
      value: "isEmpty",
      getApplyFilterFn: () => {
        return (params) => params.value == null || params.value === "";
      },
    },
    {
      label: "is not empty",
      value: "isNotEmpty",
      getApplyFilterFn: () => {
        return (params) => params.value != null && params.value !== "";
      },
    },
  ];
}

export function withGridStringOperatorsWithNotContains(
  columns: GridColDef[]
): GridColDef[] {
  const stringFilterOperators = getGridStringOperatorsWithNotContains();
  const numberFilterOperators = getGridNumberOperatorsWithZeroSupport();
  return columns.map((col) => {
    if (col.filterable === false) return col;
    const type = col.type || "string";
    if (type === "string") {
      return {
        ...col,
        filterOperators: stringFilterOperators,
      };
    }
    if (type === "number") {
      return {
        ...col,
        filterOperators: numberFilterOperators,
      };
    }
    return {
      ...col,
      filterOperators: col.filterOperators,
    };
  });
}
